import { ApiResponse, PagedResponse } from '@/types';

import { REGULATORY_JOBS_API_ENDPOINTS, REGULATORY_NOTIFICATIONS_API_ENDPOINTS } from '@/constants';
import { api } from '@/services';

import {
  IDocument,
  IIntakeDetails,
  IMessage,
  IMessageSearchingPayload,
  IResolveLinkWithDocumentPayload,
  IResolveLinkWithoutDocumentPayload,
  IRevertDocumentPayload,
} from '@/features/regulatory-intake/intake-details/interfaces';

export const IntakeDetailsService = {
  getIntakeDetails: async (id: string): Promise<IIntakeDetails> => {
    const response = await api.get<ApiResponse<IIntakeDetails>>(
      REGULATORY_JOBS_API_ENDPOINTS.JOB_DETAILS.replace(':jobId', id),
    );
    return response.data.data;
  },

  getMessagesSearching: async (
    payload: IMessageSearchingPayload,
  ): Promise<PagedResponse<IMessage>> => {
    const response = await api.post<ApiResponse<PagedResponse<IMessage>>>(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATIONS_SEARCH,
      payload,
    );
    return response.data.data;
  },

  reclassifyMessage: async (emailId: string) => {
    const response = await api.post<ApiResponse<void>>(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_RECLASSIFY.replace(':emailId', emailId),
    );
    return response.data;
  },

  jobRetry: async (jobId: string) => {
    const response = await api.post(
      REGULATORY_JOBS_API_ENDPOINTS.JOB_RETRY.replace(':jobId', jobId),
    );
    return response.data;
  },

  messageResend: async (emailId: string) => {
    const response = await api.post(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_RETRY.replace(':emailId', emailId),
    );
    return response.data;
  },

  messageResendAll: async (jobId: string) => {
    const response = await api.post(
      REGULATORY_JOBS_API_ENDPOINTS.JOBS_RETRY_FAILURE_NOTIFICATIONS.replace(':jobId', jobId),
    );
    return response.data;
  },

  getAttentionDocuments: async (emailId: string): Promise<IDocument[]> => {
    const response = await api.post<ApiResponse<IDocument[]>>(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_ATTENTION_DOCUMENTS.replace(
        ':emailId',
        emailId,
      ),
    );
    return response.data.data;
  },

  resolveWithDocument: async ({ emailId, documentId, file }: IResolveLinkWithDocumentPayload) => {
    const endpoint =
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_RESOLVE_WITH_DOCUMENT.replace(
        ':emailId',
        emailId,
      )
        .replace(':documentId', documentId)
        .replace(':fileName', file.name);

    const response = await api.post(endpoint, file, {
      headers: { 'Content-Type': 'application/octet-stream' },
      transformRequest: [(data) => data],
    });

    return response.data;
  },

  resolveWithoutDocument: async ({ emailId, documentId }: IResolveLinkWithoutDocumentPayload) => {
    const response = await api.post(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_RESOLVE_WITHOUT_DOCUMENT.replace(
        ':emailId',
        emailId,
      ).replace(':documentId', documentId),
    );
    return response.data;
  },

  transferToN1: async (emailId: string) => {
    const response = await api.post(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_TRANSFER_TO_N1.replace(
        ':emailId',
        emailId,
      ),
    );
    return response.data;
  },

  revertDocument: async ({ emailId, documentId }: IRevertDocumentPayload) => {
    const response = await api.post(
      REGULATORY_NOTIFICATIONS_API_ENDPOINTS.NOTIFICATION_REVERT_DOCUMENT.replace(
        ':emailId',
        emailId,
      ).replace(':documentId', documentId),
    );
    return response.data;
  },
};
